import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ClientInfoForm from './components/ClientInfoForm';
import FileUploadArea from './components/FileUploadArea';
import SecuritySettings from './components/SecuritySettings';
import MessageComposer from './components/MessageComposer';
import ProjectSummary from './components/ProjectSummary';

const CreateProject = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isCreating, setIsCreating] = useState(false);

  // Form data state
  const [formData, setFormData] = useState({
    projectTitle: '',
    clientId: '',
    isNewClient: true,
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: ''
  });

  const [files, setFiles] = useState([]);

  const [securitySettings, setSecuritySettings] = useState({
    screenshotPrevention: true,
    watermarkProtection: false,
    expirationTime: '24',
    customExpirationType: 'hours',
    customExpirationValue: 1,
    singleUseLink: false
  });

  const [message, setMessage] = useState('');

  // Validation errors
  const [errors, setErrors] = useState({});

  const steps = [
    { id: 1, title: 'Client Info', icon: 'User', description: 'Basic project details' },
    { id: 2, title: 'Upload Files', icon: 'Upload', description: 'Design assets' },
    { id: 3, title: 'Security', icon: 'Shield', description: 'Protection settings' },
    { id: 4, title: 'Message', icon: 'MessageSquare', description: 'Personal note' },
    { id: 5, title: 'Review', icon: 'FileCheck', description: 'Final confirmation' }
  ];

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (!formData?.projectTitle?.trim()) newErrors.projectTitle = 'Project title is required';
        
        if (!formData?.isNewClient && !formData?.clientId) {
          newErrors.clientId = 'Please select an existing client or choose to add new client';
        }
        
        if (formData?.isNewClient) {
          if (!formData?.clientName?.trim()) newErrors.clientName = 'Client name is required';
          if (!formData?.clientEmail?.trim()) newErrors.clientEmail = 'Client email is required';
          else if (!/\S+@\S+\.\S+/?.test(formData?.clientEmail)) newErrors.clientEmail = 'Invalid email format';
          
          // Phone number validation (USA format)
          if (!formData?.clientPhone?.trim()) {
            newErrors.clientPhone = 'Client phone number is required';
          } else {
            const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
            if (!phoneRegex?.test(formData?.clientPhone)) {
              newErrors.clientPhone = 'Phone number must be in format (555) 123-4567';
            }
          }

          // Address validation
          if (!formData?.streetAddress?.trim()) newErrors.streetAddress = 'Street address is required';
          if (!formData?.city?.trim()) newErrors.city = 'City is required';
          if (!formData?.state?.trim()) newErrors.state = 'State is required';
          if (!formData?.zipCode?.trim()) {
            newErrors.zipCode = 'ZIP code is required';
          } else {
            const zipRegex = /^\d{5}(-\d{4})?$/;
            if (!zipRegex?.test(formData?.zipCode)) {
              newErrors.zipCode = 'ZIP code must be in format 12345 or 12345-6789';
            }
          }
        }
        break;
      case 2:
        if (files?.length === 0) newErrors.files = 'At least one design file is required';
        else if (files?.some(f => f?.status !== 'completed')) newErrors.files = 'Please wait for all files to finish uploading';
        break;
      case 3:
        if (!securitySettings?.expirationTime) newErrors.expirationTime = 'Please select an expiration time';
        break;
      case 4:
        if (message?.length > 1000) newErrors.message = 'Message must be less than 1000 characters';
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps?.length));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleStepClick = (stepId) => {
    if (stepId <= currentStep || validateStep(currentStep)) {
      setCurrentStep(stepId);
    }
  };

  const handleEditFromSummary = (section) => {
    switch (section) {
      case 'client':
        setCurrentStep(1);
        break;
      case 'files':
        setCurrentStep(2);
        break;
      case 'security':
        setCurrentStep(3);
        break;
      case 'message':
        setCurrentStep(4);
        break;
    }
  };

  const handleCreateProject = async () => {
    if (!validateStep(5)) return;

    setIsCreating(true);
    
    try {
      // Simulate project creation
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock secure link generation
      const secureLink = `https://jewelthread.com/preview/${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}`;
      
      // Navigate to success state or dashboard
      navigate('/dashboard', { 
        state: { 
          message: 'Project created successfully! Secure link has been sent to your client.',
          projectData: { formData, files, securitySettings, message, secureLink }
        }
      });
    } catch (error) {
      console.error('Error creating project:', error);
      setErrors({ general: 'Failed to create project. Please try again.' });
    } finally {
      setIsCreating(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <ClientInfoForm
            formData={formData}
            onChange={setFormData}
            errors={errors}
          />
        );
      case 2:
        return (
          <FileUploadArea
            files={files}
            onFilesChange={setFiles}
            errors={errors}
          />
        );
      case 3:
        return (
          <SecuritySettings
            settings={securitySettings}
            onChange={setSecuritySettings}
            errors={errors}
          />
        );
      case 4:
        return (
          <MessageComposer
            message={message}
            onChange={setMessage}
            errors={errors}
          />
        );
      case 5:
        return (
          <ProjectSummary
            formData={formData}
            files={files}
            settings={securitySettings}
            message={message}
            onEdit={handleEditFromSummary}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="max-w-4xl mx-auto p-6">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/dashboard')}
              >
                <Icon name="ArrowLeft" size={20} />
              </Button>
              <div>
                <h1 className="text-2xl font-heading text-foreground">New Quote</h1>
                <p className="text-muted-foreground">Set up secure design sharing for your client</p>
              </div>
            </div>

            {/* Progress Steps */}
            <div className="flex items-center justify-between bg-card rounded-lg border border-border p-4 elevation-1">
              {steps?.map((step, index) => (
                <div key={step?.id} className="flex items-center">
                  <button
                    onClick={() => handleStepClick(step?.id)}
                    className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-luxury ${
                      currentStep === step?.id
                        ? 'bg-accent/10 text-accent'
                        : currentStep > step?.id
                        ? 'text-success hover:bg-success/5' :'text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      currentStep === step?.id
                        ? 'bg-accent text-accent-foreground'
                        : currentStep > step?.id
                        ? 'bg-success text-success-foreground'
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      {currentStep > step?.id ? (
                        <Icon name="Check" size={16} />
                      ) : (
                        <Icon name={step?.icon} size={16} />
                      )}
                    </div>
                    <div className="hidden md:block text-left">
                      <p className="text-sm font-medium">{step?.title}</p>
                      <p className="text-xs opacity-75">{step?.description}</p>
                    </div>
                  </button>
                  
                  {index < steps?.length - 1 && (
                    <div className={`hidden md:block w-8 h-px mx-2 ${
                      currentStep > step?.id ? 'bg-success' : 'bg-border'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Step Content */}
          <div className="mb-8">
            {renderStepContent()}
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between bg-card rounded-lg border border-border p-6 elevation-1">
            <div className="flex items-center space-x-4">
              {currentStep > 1 && (
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  iconName="ChevronLeft"
                  iconPosition="left"
                >
                  Previous
                </Button>
              )}
            </div>

            <div className="flex items-center space-x-4">
              {currentStep < steps?.length ? (
                <Button
                  onClick={handleNext}
                  iconName="ChevronRight"
                  iconPosition="right"
                >
                  Next Step
                </Button>
              ) : (
                <Button
                  onClick={handleCreateProject}
                  loading={isCreating}
                  iconName="Send"
                  iconPosition="left"
                  variant="default"
                >
                  {isCreating ? 'Creating Project...' : 'Create Secure Link'}
                </Button>
              )}
            </div>
          </div>

          {/* Error Display */}
          {errors?.general && (
            <div className="mt-4 p-4 bg-error/10 border border-error/20 rounded-lg">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={16} color="var(--color-error)" />
                <p className="text-sm text-error">{errors?.general}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateProject;